import { supabase, isSupabaseConfigured } from './supabase'

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || 'AIzaSyDJzPfKuiJ_FYxLkGoeTIO2kEZ2NkOLKKo'
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash:generateContent'

/**
 * Check if current user is Pro subscriber
 */
export async function checkIsProUser() {
    if (!isSupabaseConfigured()) return false

    try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return false

        // Check subscriptions table
        const { data: subscription } = await supabase
            .from('subscriptions')
            .select('status, plan')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .eq('plan', 'pro')
            .single()

        return !!subscription
    } catch (error) {
        console.error('Error checking Pro status:', error)
        return false
    }
}

/**
 * Enhanced cliché detection using Gemini AI
 * @param {string} text - The text to analyze
 * @returns {Promise<{detections: Array}>} Detection results
 */
export async function detectWithAI(text) {
    if (!text || text.trim().length < 10) {
        return { detections: [] }
    }

    try {
        const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents: [{
                    parts: [{
                        text: `Analyze this text for clichés, overused phrases, and weak writing patterns. 
            Return ONLY valid JSON with no markdown formatting or explanation.
            
            Format: {"detections": [{"phrase": "exact phrase from text", "reason": "why it's a cliché", "suggestion": "better alternative", "severity": "high/medium/low"}]}
            
            If no clichés are found, return: {"detections": []}
            
            Text to analyze: "${text.slice(0, 2000)}"` // Limit text length
                    }]
                }],
                generationConfig: {
                    temperature: 0.3,
                    maxOutputTokens: 2000
                }
            })
        })

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`)
        }

        const result = await response.json()
        const content = result.candidates?.[0]?.content?.parts?.[0]?.text

        if (!content) {
            return { detections: [] }
        }

        // Parse JSON from response (handle markdown code blocks)
        let jsonString = content
        if (content.includes('```json')) {
            jsonString = content.replace(/```json\n?/g, '').replace(/```\n?/g, '')
        } else if (content.includes('```')) {
            jsonString = content.replace(/```\n?/g, '')
        }

        const parsed = JSON.parse(jsonString.trim())
        return parsed
    } catch (error) {
        console.error('Gemini AI detection error:', error)
        return { detections: [], error: error.message }
    }
}

/**
 * Get AI-powered alternative suggestions for a phrase
 * @param {string} phrase - The cliché phrase
 * @param {string} context - Surrounding context
 * @returns {Promise<{alternatives: Array}>}
 */
export async function getAlternatives(phrase, context = '') {
    try {
        const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents: [{
                    parts: [{
                        text: `Generate 5 creative, natural alternatives for this overused phrase.
            Consider the context and maintain the same meaning.
            Return ONLY valid JSON with no markdown formatting.
            
            Phrase: "${phrase}"
            ${context ? `Context: "${context}"` : ''}
            
            Format: {"alternatives": ["alternative 1", "alternative 2", "alternative 3", "alternative 4", "alternative 5"]}`
                    }]
                }],
                generationConfig: {
                    temperature: 0.7,
                    maxOutputTokens: 500
                }
            })
        })

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`)
        }

        const result = await response.json()
        const content = result.candidates?.[0]?.content?.parts?.[0]?.text

        if (!content) {
            return { alternatives: [] }
        }

        let jsonString = content
        if (content.includes('```json')) {
            jsonString = content.replace(/```json\n?/g, '').replace(/```\n?/g, '')
        } else if (content.includes('```')) {
            jsonString = content.replace(/```\n?/g, '')
        }

        const parsed = JSON.parse(jsonString.trim())
        return parsed
    } catch (error) {
        console.error('Get alternatives error:', error)
        return { alternatives: [], error: error.message }
    }
}

/**
 * Analyze overall writing style
 * @param {string} text - Full text to analyze
 * @returns {Promise<Object>} Style analysis
 */
export async function analyzeStyle(text) {
    if (!text || text.trim().length < 50) {
        return { error: 'Text too short for analysis' }
    }

    try {
        const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents: [{
                    parts: [{
                        text: `Analyze this text's writing style and quality.
            Return ONLY valid JSON with no markdown formatting.
            
            Format: {
              "overallScore": 0-100,
              "tone": "professional/casual/academic/creative/other",
              "readability": "easy/moderate/difficult",
              "clicheCount": number,
              "strengths": ["strength 1", "strength 2"],
              "improvements": ["improvement 1", "improvement 2"],
              "wordCount": number,
              "sentenceCount": number,
              "averageWordsPerSentence": number
            }
            
            Text: "${text.slice(0, 3000)}"`
                    }]
                }],
                generationConfig: {
                    temperature: 0.3,
                    maxOutputTokens: 1000
                }
            })
        })

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`)
        }

        const result = await response.json()
        const content = result.candidates?.[0]?.content?.parts?.[0]?.text

        if (!content) {
            return { error: 'No analysis returned' }
        }

        let jsonString = content
        if (content.includes('```json')) {
            jsonString = content.replace(/```json\n?/g, '').replace(/```\n?/g, '')
        } else if (content.includes('```')) {
            jsonString = content.replace(/```\n?/g, '')
        }

        const parsed = JSON.parse(jsonString.trim())
        return parsed
    } catch (error) {
        console.error('Style analysis error:', error)
        return { error: error.message }
    }
}

/**
 * Rate limit helper - checks Pro status for unlimited usage
 */
const RATE_LIMIT_KEY = 'inkspot_ai_requests'
const MAX_FREE_REQUESTS = 10

// Cache Pro status to avoid repeated DB calls
let cachedProStatus = null
let proStatusCheckedAt = 0

export async function checkRateLimitAsync() {
    const today = new Date().toDateString()

    // Check Pro status (cache for 5 minutes)
    const now = Date.now()
    if (cachedProStatus === null || now - proStatusCheckedAt > 5 * 60 * 1000) {
        cachedProStatus = await checkIsProUser()
        proStatusCheckedAt = now
    }

    // Pro users have unlimited usage
    if (cachedProStatus) {
        return { allowed: true, remaining: Infinity, isPro: true }
    }

    // Free user rate limiting
    const stored = localStorage.getItem(RATE_LIMIT_KEY)

    if (!stored) {
        return { allowed: true, remaining: MAX_FREE_REQUESTS, isPro: false }
    }

    const { date, count } = JSON.parse(stored)

    if (date !== today) {
        // New day, reset counter
        return { allowed: true, remaining: MAX_FREE_REQUESTS, isPro: false }
    }

    return {
        allowed: count < MAX_FREE_REQUESTS,
        remaining: Math.max(0, MAX_FREE_REQUESTS - count),
        isPro: false
    }
}

// Synchronous version for initial render (uses cached value)
export function checkRateLimit() {
    const today = new Date().toDateString()

    // If we know user is Pro from cache
    if (cachedProStatus === true) {
        return { allowed: true, remaining: Infinity, isPro: true }
    }

    // Free user rate limiting
    const stored = localStorage.getItem(RATE_LIMIT_KEY)

    if (!stored) {
        return { allowed: true, remaining: MAX_FREE_REQUESTS, isPro: false }
    }

    const { date, count } = JSON.parse(stored)

    if (date !== today) {
        return { allowed: true, remaining: MAX_FREE_REQUESTS, isPro: false }
    }

    return {
        allowed: count < MAX_FREE_REQUESTS,
        remaining: Math.max(0, MAX_FREE_REQUESTS - count),
        isPro: false
    }
}

export function incrementRateLimit() {
    // Pro users don't increment counter
    if (cachedProStatus === true) return

    const today = new Date().toDateString()
    const stored = localStorage.getItem(RATE_LIMIT_KEY)

    let count = 1
    if (stored) {
        const data = JSON.parse(stored)
        if (data.date === today) {
            count = data.count + 1
        }
    }

    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({
        date: today,
        count
    }))
}

// Reset Pro status cache (call when subscription changes)
export function resetProStatusCache() {
    cachedProStatus = null
    proStatusCheckedAt = 0
}
